import Decimal from 'decimal.js';
import { haversineMeters } from './driver-attendance';
import { parseShippingLocation } from './shipping';

/**
 * P12 §16 – Xác nhận giờ cẩu (anh Tiến duyệt 11/10/2026, đề xuất mục 3; DEC-291).
 * Ba nguồn kiểm chéo: (1) tài xế bấm "Bắt đầu cẩu" / "Xong cẩu" trên phiếu giao, mỗi lần 1 ảnh + định vị điện thoại;
 * (2) khách ký nhận trên màn Giao xong có câu "Cẩu từ … đến … (… phút)"; (3) GPS Bình Minh: xe đứng yên trong khoảng cẩu.
 * Dầu cẩu = phút cẩu × định mức lít/giờ cẩu của xe (chỉ quản lý thấy), trừ ra trước khi so tiêu hao theo km.
 */
const D = Decimal.clone({ rounding: Decimal.ROUND_HALF_UP });

/** Hai lần bấm phải cách điểm giao dưới 300 m (vượt vẫn cho bấm, gắn cờ). */
export const CRANE_RADIUS_M = 300;
/** GPS Bình Minh: tốc độ > 5 km/h trong khoảng cẩu = xe đang chạy */
export const CRANE_MOVING_SPEED_KMH = 5;
/** GPS Bình Minh: km trong ngày tăng > 1 km trong khoảng cẩu = xe đã chạy */
export const CRANE_MOVING_KM = 1;
/**
 * Anh Tiến 23/10/2026 "cẩu xong quên bấm thì sao": cẩu phải nổ máy (bơm thủy lực chạy bằng máy xe).
 * GPS báo máy tắt (Đỗ tắt máy, theo khóa điện + điện áp, gpsEngineOn) liên tục từ số phút này → hệ thống tự chốt Xong cẩu lúc máy tắt.
 */
export const CRANE_ENGINE_OFF_MINUTES = 5;
/** Đang cẩu quá số phút này mà chưa bấm Xong → nhắc tài xế 1 lần (cẩu xong thì bấm Xong, còn cẩu thì bỏ qua) */
export const CRANE_REMIND_MINUTES = 60;

export const CRANE_FLAGS = ['open', 'no_photo', 'no_location', 'far', 'apart', 'gps_moving', 'engine_off', 'no_signature', 'auto_closed', 'engine_closed', 'no_note'] as const;
export type CraneFlag = (typeof CRANE_FLAGS)[number];
export const CRANE_FLAG_LABELS: Record<CraneFlag, string> = {
  open: 'Chưa bấm Xong cẩu',
  no_photo: 'Thiếu ảnh',
  no_location: 'Thiếu định vị điện thoại',
  far: 'Ngoài 300 m chỗ giao',
  apart: 'Bắt đầu và xong cẩu không cùng chỗ',
  gps_moving: 'GPS: xe chạy trong lúc cẩu',
  engine_off: 'GPS: máy tắt trong lúc cẩu',
  no_signature: 'Khách chưa ký xác nhận',
  auto_closed: 'Quên bấm Xong, hệ thống chốt theo GPS',
  engine_closed: 'Quên bấm Xong, hệ thống chốt lúc tắt máy',
  no_note: 'Cẩu ngoài phiếu giao',
};

export const CRANE_END_SOURCES = ['driver', 'gps', 'engine', 'office'] as const;
export type CraneEndSource = (typeof CRANE_END_SOURCES)[number];
export const CRANE_END_SOURCE_LABELS: Record<CraneEndSource, string> = {
  driver: 'Tài xế bấm',
  gps: 'Tự chốt theo GPS',
  engine: 'Tự chốt lúc tắt máy',
  office: 'Quản lý chốt',
};

export interface LatLng {
  lat: number;
  lng: number;
}

const VN_MS = 7 * 3_600_000;
const vnParts = (d: Date | string) => new Date(new Date(d).getTime() + VN_MS).toISOString();
/** HH:mm giờ VN */
export const vnHhmm = (d: Date | string) => vnParts(d).slice(11, 16);
const vnDay = (d: Date | string) => vnParts(d).slice(0, 10);
const floorMinute = (d: Date | string) => Math.floor(new Date(d).getTime() / 60_000);

/** Số phút cẩu: tính theo phút đã cắt giây của 2 mốc, để khớp đúng giờ HH:mm hiện trên màn ký. */
export function craneMinutes(startedAt: Date | string, endedAt: Date | string): number {
  return Math.max(0, floorMinute(endedAt) - floorMinute(startedAt));
}

/** Câu hiện trên màn ký nhận và lưu lại lúc khách ký: "Cẩu từ 09:10 đến 10:25 (75 phút)". */
export function craneSpanText(startedAt: Date | string, endedAt: Date | string): string {
  const end = vnDay(startedAt) === vnDay(endedAt) ? vnHhmm(endedAt) : `${vnHhmm(endedAt)} ngày ${vnDay(endedAt).slice(8)}/${vnDay(endedAt).slice(5, 7)}`;
  return `Cẩu từ ${vnHhmm(startedAt)} đến ${end} (${craneMinutes(startedAt, endedAt)} phút)`;
}

/** Lít dầu cẩu = phút × lít/giờ ÷ 60, 2 số lẻ ROUND_HALF_UP. Chưa có định mức cẩu → null (không trừ). */
export function craneLitres(minutes: number, litresPerHour: number | null | undefined): number | null {
  if (!litresPerHour || litresPerHour <= 0) return null;
  if (!(minutes > 0)) return 0;
  return new D(minutes).times(litresPerHour).div(60).toDecimalPlaces(2).toNumber();
}

/**
 * Dòng dầu cẩu trên khoán dầu tháng cho quản lý (DEC-291 bổ sung): "Dầu cẩu: 12,5 giờ × 6 L/giờ = 75 L".
 * Chỉ dùng ở màn quản lý; tài xế chỉ được thấy giờ cẩu (craneHoursText), không thấy L/giờ.
 */
export function craneAllowanceText(minutes: number, litresPerHour: number, litres: number): string {
  return `Dầu cẩu: ${craneHoursText(minutes)} × ${litresPerHour.toLocaleString('vi-VN')} L/giờ = ${litres.toLocaleString('vi-VN')} L`;
}
/** "12,5 giờ" (phút ÷ 60, 2 số lẻ ROUND_HALF_UP) */
export function craneHoursText(minutes: number): string {
  return `${new D(minutes).div(60).toDecimalPlaces(2).toNumber().toLocaleString('vi-VN')} giờ`;
}

/**
 * Điểm giao để đo 300 m: chỉ khi ô địa chỉ giao có tọa độ ("10.8456, 106.7512") hoặc link Google Maps đầy đủ có tọa độ.
 * Địa chỉ chữ / link rút gọn → null (không gọi bản đồ ngoài lúc tài xế bấm); khi đó so với vị trí lúc khách ký nhận.
 */
export function craneDestination(address: string | null | undefined): LatLng | null {
  if (!address) return null;
  const loc = parseShippingLocation(address);
  return loc && loc.kind === 'latlng' ? { lat: loc.lat, lng: loc.lng } : null;
}

const dist = (a: LatLng | null, b: LatLng | null) => (a && b ? Math.round(haversineMeters(a.lat, a.lng, b.lat, b.lng)) : null);

export interface CraneGpsPosition {
  at: Date | string;
  lat: number;
  lng: number;
  speedKmh: number | null;
}
export interface CraneGpsSample {
  at: Date | string;
  /** Km trong ngày tại lúc lấy mẫu (GPS về 0 lúc nửa đêm giờ VN) */
  km: number;
}
export type CraneGpsStatus = 'still' | 'moving' | 'no_data';
export interface CraneGpsCheck {
  status: CraneGpsStatus;
  /** Số điểm GPS (tọa độ + mẫu km) nằm trong khoảng cẩu */
  points: number;
  /** Xa nhất khỏi điểm GPS đầu tiên trong khoảng (m) */
  maxMoveM: number | null;
  maxSpeedKmh: number | null;
  kmDelta: number | null;
  /** Phút GPS báo máy tắt (Đỗ tắt máy) trong khoảng cẩu; không có mẫu trạng thái → null */
  engineOffMinutes?: number | null;
}

/**
 * GPS Bình Minh trong khoảng cẩu [bắt đầu, xong): xe phải đứng yên. Chỉ dùng điểm nằm TRONG khoảng (điểm trước / sau có thể là lúc xe chạy tới / đi;
 * điểm đúng giờ Xong không tính vì lượt tự chốt theo GPS lấy chính điểm xe bắt đầu chạy làm giờ Xong).
 * - Tọa độ: tốc độ > 5 km/h hoặc lệch > 300 m so với điểm đầu → chạy.
 * - Mẫu km trong ngày (cùng ngày VN): tăng > 1 km → chạy.
 * Hộp đen Bình Minh không trả tín hiệu trích công suất (PTO); máy nổ / tắt kiểm riêng theo trạng thái GPS (craneEngineOffMinutes).
 * Không có điểm nào → no_data (bỏ qua, không gắn cờ).
 */
export function craneGpsCheck(i: { startedAt: Date | string; endedAt: Date | string; positions: CraneGpsPosition[]; samples: CraneGpsSample[] }): CraneGpsCheck {
  const from = new Date(i.startedAt).getTime();
  const to = new Date(i.endedAt).getTime();
  const inside = <T extends { at: Date | string }>(xs: T[]) =>
    xs.filter((x) => new Date(x.at).getTime() >= from && new Date(x.at).getTime() < to).sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const pos = inside(i.positions);
  const smp = inside(i.samples);
  let maxMoveM: number | null = null;
  let maxSpeedKmh: number | null = null;
  if (pos.length) {
    const base = pos[0]!;
    maxMoveM = Math.max(...pos.map((p) => Math.round(haversineMeters(base.lat, base.lng, p.lat, p.lng))));
    const speeds = pos.map((p) => p.speedKmh).filter((s): s is number => s !== null && s !== undefined);
    maxSpeedKmh = speeds.length ? Math.max(...speeds) : null;
  }
  let kmDelta: number | null = null;
  if (smp.length >= 2) {
    const byDay = new Map<string, number[]>();
    for (const s of smp) byDay.set(vnDay(s.at), [...(byDay.get(vnDay(s.at)) ?? []), Number(s.km)]);
    kmDelta = Math.round([...byDay.values()].reduce((sum, ks) => sum + (Math.max(...ks) - Math.min(...ks)), 0) * 10) / 10;
  }
  const points = pos.length + smp.length;
  if (!pos.length && kmDelta === null) return { status: 'no_data', points, maxMoveM, maxSpeedKmh, kmDelta };
  const moving = (maxMoveM ?? 0) > CRANE_RADIUS_M || (maxSpeedKmh ?? 0) > CRANE_MOVING_SPEED_KMH || (kmDelta ?? 0) > CRANE_MOVING_KM;
  return { status: moving ? 'moving' : 'still', points, maxMoveM, maxSpeedKmh, kmDelta };
}

export interface CraneSessionFacts {
  startedAt: Date | string;
  endedAt: Date | string | null;
  start: LatLng | null;
  end: LatLng | null;
  startPhotoId: number | null;
  endPhotoId: number | null;
  endSource: CraneEndSource | null;
  /** Khách đã ký nhận trên màn có câu giờ cẩu */
  customerConfirmed: boolean;
  /** Tọa độ trong ô địa chỉ giao (craneDestination) */
  destination: LatLng | null;
  /** Vị trí điện thoại lúc khách ký nhận (POD), dùng khi địa chỉ giao không có tọa độ */
  podPosition: LatLng | null;
  gps: CraneGpsCheck | null;
  /** Có phiếu giao (false = cẩu ngoài phiếu: ở kho, dự án…, anh Tiến 23/10/2026) */
  hasNote?: boolean;
}
export interface CraneSessionCheck {
  minutes: number | null;
  /** Mốc đo 300 m: tọa độ địa chỉ giao, vị trí ký nhận, hoặc chưa có */
  basis: 'address' | 'pod' | null;
  startDistM: number | null;
  endDistM: number | null;
  /** Khoảng cách giữa điểm bấm Bắt đầu và Xong */
  apartM: number | null;
  flags: CraneFlag[];
}

/** Cờ của 1 lượt cẩu cho bảng giờ cẩu tháng của quản lý (tính lại mỗi lần xem, không lưu). */
export function craneSessionCheck(s: CraneSessionFacts): CraneSessionCheck {
  const ref = s.destination ?? s.podPosition;
  const basis = s.destination ? 'address' : s.podPosition ? 'pod' : null;
  const startDistM = dist(s.start, ref);
  const endDistM = dist(s.end, ref);
  const apartM = dist(s.start, s.end);
  const flags: CraneFlag[] = [];
  if (!s.endedAt) flags.push('open');
  if (!s.startPhotoId || (s.endedAt && !s.endPhotoId)) flags.push('no_photo');
  if (!s.start || (s.endedAt && s.endSource !== 'gps' && s.endSource !== 'engine' && !s.end)) flags.push('no_location');
  if ((startDistM ?? 0) > CRANE_RADIUS_M || (endDistM ?? 0) > CRANE_RADIUS_M) flags.push('far');
  if ((apartM ?? 0) > CRANE_RADIUS_M) flags.push('apart');
  if (s.gps?.status === 'moving') flags.push('gps_moving');
  if ((s.gps?.engineOffMinutes ?? 0) >= CRANE_ENGINE_OFF_MINUTES) flags.push('engine_off');
  const hasNote = s.hasNote !== false;
  if (hasNote && s.endedAt && !s.customerConfirmed) flags.push('no_signature');
  if (s.endSource === 'gps') flags.push('auto_closed');
  if (s.endSource === 'engine') flags.push('engine_closed');
  if (!hasNote) flags.push('no_note');
  return { minutes: s.endedAt ? craneMinutes(s.startedAt, s.endedAt) : null, basis, startDistM, endDistM, apartM, flags };
}

export interface CraneEngineSample {
  at: Date | string;
  /** Trạng thái GPS đã lưu (gps-status.ts): 'parked' = đứng, máy tắt */
  status: string;
}
const engineOff = (st: string) => st === 'parked';

/** Phút máy tắt trong [bắt đầu, xong): mỗi mẫu 'parked' kéo tới mẫu sau (tối đa 10 phút). Không có mẫu → null. */
export function craneEngineOffMinutes(startedAt: Date | string, endedAt: Date | string, samples: CraneEngineSample[]): number | null {
  const from = new Date(startedAt).getTime();
  const to = new Date(endedAt).getTime();
  const s = samples.map((x) => ({ t: new Date(x.at).getTime(), off: engineOff(x.status) })).filter((x) => x.t >= from && x.t < to).sort((a, b) => a.t - b.t);
  if (!s.length) return null;
  let ms = 0;
  s.forEach((x, k) => {
    if (x.off) ms += Math.min((s[k + 1]?.t ?? to) - x.t, 10 * 60_000);
  });
  return Math.round(ms / 60_000);
}

/**
 * Lượt cẩu còn mở: lúc máy tắt để tự chốt Xong (anh Tiến 23/10/2026 "cẩu xong quên bấm thì sao").
 * Mẫu GPS sau giờ bắt đầu: lượt máy tắt (các mẫu 'parked' liền nhau) kéo dài ít nhất CRANE_ENGINE_OFF_MINUTES
 * (tính tới mẫu kế tiếp không còn tắt, hoặc tới mẫu tắt cuối cùng nếu vẫn đang tắt) → trả giờ mẫu tắt đầu tiên.
 * Tắt máy ngắn rồi nổ lại (chờ người, đổi chỗ chân chống) → không chốt.
 */
export function craneEngineOffAt(startedAt: Date | string, samples: CraneEngineSample[]): Date | null {
  const from = new Date(startedAt).getTime();
  const s = samples.map((x) => ({ t: new Date(x.at).getTime(), off: engineOff(x.status) })).filter((x) => x.t > from).sort((a, b) => a.t - b.t);
  const need = CRANE_ENGINE_OFF_MINUTES * 60_000;
  for (let k = 0; k < s.length; k++) {
    if (!s[k]!.off || (k > 0 && s[k - 1]!.off)) continue;
    let j = k;
    while (j + 1 < s.length && s[j + 1]!.off) j++;
    const until = s[j + 1]?.t ?? s[j]!.t;
    if (until - s[k]!.t >= need) return new Date(s[k]!.t);
  }
  return null;
}

/** Câu cho tài xế ngay lúc bấm, khi biết điểm giao và đang xa hơn 300 m (vẫn lưu, quản lý kiểm). */
export function craneFarWarning(distM: number | null): string | null {
  return distM !== null && distM > CRANE_RADIUS_M ? `Bạn đang cách chỗ giao khoảng ${distM.toLocaleString('vi-VN')} m (quá ${CRANE_RADIUS_M} m). Đã ghi lại, quản lý sẽ kiểm tra.` : null;
}
